import { Form } from '@/components/forms';
import { EmailInput } from '@/components/forms/email-input/component';
import { Flex, Text, TextInput } from '@mantine/core';
import { FormikProps } from 'formik';
import { useEffect } from 'react';
import * as yup from 'yup';
import styles from './styles.module.css';

const validationSchema = yup.object({
	firstName: yup.string().required('First name is required').max(40, 'First name is too long'), // 40 chars Salesforce limitation
	lastName: yup.string().required('Last name is required').max(40, 'Last name is too long'), // 40 chars Salesforce limitation
	email: yup
		.string()
		.email('Invalid email address')
		.required('Email is required')
		.max(80, 'Email is too long'), // Salesforce limitation
});

export type AccountFormValues = {
	firstName: string;
	lastName: string;
	email: string;
};

type AccountFormProps = {
	onSubmit: (values: AccountFormValues) => void;
	formRef: React.RefObject<FormikProps<AccountFormValues>>;
	initialValues: AccountFormValues;
	setShouldSubmit: React.Dispatch<React.SetStateAction<boolean>>;
};

export const AccountForm = ({
	formRef,
	onSubmit,
	initialValues,
	setShouldSubmit,
}: AccountFormProps) => {
	return (
		<Form
			initialValues={initialValues}
			onSubmit={onSubmit}
			innerRef={formRef}
			validationSchema={validationSchema}
		>
			{(formikProps) => {
				return (
					<FormFields
						{...formikProps}
						initialValues={initialValues}
						setShouldSubmit={setShouldSubmit}
					/>
				);
			}}
		</Form>
	);
};

const FormFields = ({
	getFieldProps,
	touched,
	errors,
	setShouldSubmit,
	isValid,
	dirty,
}: FormikProps<AccountFormValues> & {
	setShouldSubmit: React.Dispatch<React.SetStateAction<boolean>>;
	initialValues: AccountFormValues;
}) => {
	useEffect(() => {
		const shouldEnableSubmit = isValid && dirty;

		setShouldSubmit(shouldEnableSubmit);
	}, [setShouldSubmit, isValid, dirty]);

	return (
		<>
			<Text fz="24px" fw={900}>
				Basic Details
			</Text>
			<Flex gap="md" w="100%">
				<TextInput
					label="First Name"
					placeholder="First Name"
					className={styles.cell}
					{...getFieldProps('firstName')}
					error={touched.firstName && errors.firstName}
				/>
				<TextInput
					label="Last Name"
					placeholder="Last Name"
					className={styles.cell}
					{...getFieldProps('lastName')}
					error={touched.lastName && errors.lastName}
				/>
			</Flex>
			<EmailInput
				label="Email"
				placeholder="Email"
				{...getFieldProps('email')}
				error={touched.email && errors.email}
			/>
		</>
	);
};
